/**
 * R232q5ad QuestTurnInModal — parchment-scroll modal that pops on quest
 * completion. Shown when:
 *   - the user clicks "Сдать квест" in ServerQuestLog and the result is ok
 *   - the server emits a `show_quest_turnin` side-effect via NPC dialogue
 *   - any code dispatches `aiwari-quest-complete` window CustomEvent
 *
 * Activation event: `aiwari-quest-complete`
 *   detail: { questId, title?, completionText?, rewards?: QuestRewards }
 *
 * Renders the same parchment look as QuestAcceptModal, headlines "Награда",
 * lists rewards with their iconographic chips, and a single gold "Получить
 * награду [Z]" button. On confirm: gold-spark CSS particles bloom from the
 * scroll center, audio plays a quest-complete jingle, then the modal closes.
 *
 * R232q5aK: when `rewards.item_choice` has 2+ entries the modal renders an
 * ITEM CHOICE grid above the gold/xp chip list. The player must click one
 * tile to enable the "Получить награду" button. The selected index is
 * stored in `selectedChoiceIdx` and passed back via the `aiwari-quest-claim`
 * event so the consumer can forward it to `quest-turn-in`.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { audio } from '../audio/AudioManager.js';

interface QuestRewards {
  xp: number;
  gold_aurei?: number;
  crystals?: number;
  faction_id?: string;
  faction_rep?: number;
  item_choice?: Array<{ item_id: string; name?: string }>;
}

interface QuestCompleteDetail {
  questId: string;
  title?: string;
  completionText?: string;
  rewards?: QuestRewards;
}

export function QuestTurnInModal() {
  const [open, setOpen] = useState<QuestCompleteDetail | null>(null);
  const [unfurled, setUnfurled] = useState(false);
  const [sparking, setSparking] = useState(false);
  /** R232q5aK: which item the player selected from `item_choice` grid. */
  const [selectedChoiceIdx, setSelectedChoiceIdx] = useState<number | null>(null);
  const closeTimer = useRef<number | null>(null);

  // Listen for completion events.
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<QuestCompleteDetail>).detail;
      if (!detail?.questId) return;
      setOpen(detail);
      setUnfurled(false);
      setSparking(false);
      // R232q5aK: pre-select the only choice automatically when the
      // server returned exactly one item_choice entry; player still has
      // to confirm with [Z], but the picker isn't required.
      const single = (detail.rewards?.item_choice?.length ?? 0) === 1;
      setSelectedChoiceIdx(single ? 0 : null);
    };
    window.addEventListener('aiwari-quest-complete', handler as EventListener);
    return () => window.removeEventListener('aiwari-quest-complete', handler as EventListener);
  }, []);

  // Unfurl animation on mount.
  useEffect(() => {
    if (!open) return;
    const id = window.requestAnimationFrame(() => setUnfurled(true));
    return () => window.cancelAnimationFrame(id);
  }, [open]);

  // Cleanup pending close timer on unmount / open-change.
  useEffect(() => {
    return () => {
      if (closeTimer.current !== null) {
        window.clearTimeout(closeTimer.current);
        closeTimer.current = null;
      }
    };
  }, []);

  // Keybinding: Z to claim, Esc to dismiss without claim.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement | null)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;
      if (e.key === 'Escape') {
        e.preventDefault();
        close();
      } else if (e.key === 'z' || e.key === 'Z' || e.key === 'я' || e.key === 'Я') {
        e.preventDefault();
        claim();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, sparking]);

  const close = () => {
    if (closeTimer.current !== null) {
      window.clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
    setOpen(null);
    setUnfurled(false);
    setSparking(false);
    setSelectedChoiceIdx(null);
  };

  const claim = () => {
    if (!open || sparking) return;
    // R232q5aK: enforce reward-picker contract — block claim until a
    // choice is selected when 2+ items are offered.
    const choices = open.rewards?.item_choice ?? [];
    if (choices.length > 1 && selectedChoiceIdx === null) return;
    setSparking(true);
    audio.playNotify('quest-complete');
    // R232q5aK: emit a separate "claim" event so the consumer (typically
    // ServerQuestLog) can forward `quest-turn-in` with rewardChoiceIndex.
    window.dispatchEvent(new CustomEvent('aiwari-quest-claim', {
      detail: {
        questId: open.questId,
        rewardChoiceIndex: selectedChoiceIdx,
      },
    }));
    closeTimer.current = window.setTimeout(() => { close(); }, 1100);
  };

  const sparks = useMemo(() => {
    // 14 spark glyphs scattered on a circle around the scroll center.
    const arr: Array<{ angle: number; dist: number; delay: number; size: number }> = [];
    for (let i = 0; i < 14; i++) {
      const angle = (Math.PI * 2 * i) / 14 + (i % 2 ? 0.18 : 0);
      const dist = 130 + (i % 3) * 45;
      const delay = (i % 5) * 60;
      const size = 8 + (i % 4) * 3;
      arr.push({ angle, dist, delay, size });
    }
    return arr;
  }, []);

  if (!open) return null;
  const rewards = open.rewards;

  return (
    <div
      className="fixed inset-0 z-[95] flex items-center justify-center p-6"
      style={{
        background: 'radial-gradient(circle at center, rgba(11,9,7,0.82) 0%, rgba(0,0,0,0.94) 90%)',
        backdropFilter: 'blur(5px)',
      }}
      onClick={(e) => { if (e.target === e.currentTarget && !sparking) close(); }}
    >
      {/* Embedded keyframes — kept inline so the component is self-contained. */}
      <style>{`
        @keyframes aiwari-spark-burst {
          0%   { opacity: 0; transform: translate(0, 0) scale(0.4); }
          15%  { opacity: 1; }
          80%  { opacity: 1; }
          100% { opacity: 0; transform: translate(var(--dx), var(--dy)) scale(1.2); }
        }
        @keyframes aiwari-scroll-glow {
          0%, 100% { box-shadow: 0 0 60px rgba(201,169,106,0.45), inset 0 0 50px rgba(92,69,32,0.2); }
          50%      { box-shadow: 0 0 90px rgba(232,199,123,0.7),  inset 0 0 70px rgba(201,169,106,0.35); }
        }
      `}</style>

      <div
        className="relative max-w-[600px] w-[92vw] max-h-[86vh] flex flex-col overflow-visible"
        style={{
          background: 'linear-gradient(180deg, #E8DCC0 0%, #D4C29C 100%)',
          border: '3px solid #5C4520',
          boxShadow: '0 0 60px rgba(201,169,106,0.45), inset 0 0 50px rgba(92,69,32,0.2)',
          fontFamily: 'EB Garamond, Georgia, serif',
          color: '#3A2818',
          transform: unfurled ? 'scaleY(1)' : 'scaleY(0.02)',
          transformOrigin: 'top center',
          transition: 'transform 420ms cubic-bezier(0.22, 1, 0.36, 1)',
          animation: sparking ? 'aiwari-scroll-glow 1100ms ease-out' : undefined,
        }}
      >
        <Corner pos="tl" />
        <Corner pos="tr" />
        <Corner pos="bl" />
        <Corner pos="br" />

        {/* Wax seal in heroic gold for completion. */}
        <div className="flex justify-center pt-6 relative">
          <div
            className="flex items-center justify-center text-[26px] relative"
            style={{
              width: 64,
              height: 64,
              borderRadius: '50%',
              background: 'radial-gradient(circle at 32% 28%, #F8E6B5, #E8C77B 65%, #8A6B2E 100%)',
              border: '3px solid #3A2818',
              boxShadow: '0 6px 16px rgba(0,0,0,0.5), inset 0 -4px 8px rgba(0,0,0,0.35), 0 0 28px rgba(232,199,123,0.6)',
              color: '#0B0907',
              fontFamily: 'Cinzel, serif',
              fontWeight: 800,
              textShadow: '0 1px 0 rgba(255,255,255,0.25)',
            }}
            aria-hidden
          >
            ✦
          </div>

          {/* Spark particles — only render when claimed. */}
          {sparking && sparks.map((s, i) => {
            const dx = Math.cos(s.angle) * s.dist;
            const dy = Math.sin(s.angle) * s.dist;
            return (
              <span
                key={i}
                className="pointer-events-none absolute"
                style={{
                  top: 32,
                  left: '50%',
                  width: s.size,
                  height: s.size,
                  borderRadius: '50%',
                  background: 'radial-gradient(circle, #F8E6B5 0%, #E8C77B 40%, rgba(201,169,106,0) 70%)',
                  // CSS custom props consumed by the keyframe.
                  ['--dx' as string]: `${dx}px`,
                  ['--dy' as string]: `${dy}px`,
                  animation: `aiwari-spark-burst 900ms ${s.delay}ms cubic-bezier(0.22, 1, 0.36, 1) forwards`,
                  filter: 'drop-shadow(0 0 6px rgba(232,199,123,0.9))',
                } as React.CSSProperties}
              />
            );
          })}
        </div>

        {/* Header */}
        <div className="px-8 pt-3 pb-4 text-center">
          <div
            className="text-[10px] uppercase tracking-[0.32em] mb-1"
            style={{ fontFamily: 'Spectral SC, serif', color: '#8A6B2E' }}
          >
            Поручение исполнено
          </div>
          <h2
            className="text-[28px] leading-tight"
            style={{
              fontFamily: 'Cinzel, serif',
              fontWeight: 700,
              color: '#3A2818',
              letterSpacing: '0.04em',
            }}
          >
            Награда
          </h2>
          {open.title && (
            <div
              className="text-[12px] italic mt-1"
              style={{ fontFamily: 'EB Garamond, serif', color: '#5C4520' }}
            >
              «{open.title}»
            </div>
          )}
        </div>

        <Divider />

        {/* Body */}
        <div className="px-8 py-5 overflow-y-auto flex-1">
          {open.completionText && (
            <p
              className="text-[16px] leading-relaxed italic mb-5 text-center"
              style={{ fontFamily: 'EB Garamond, serif', color: '#3A2818' }}
            >
              «{open.completionText}»
            </p>
          )}

          {/* R232q5aK: reward picker grid — only when 2+ choice items. */}
          {(rewards?.item_choice?.length ?? 0) > 1 && (
            <ChoicePicker
              choices={rewards!.item_choice!}
              selectedIdx={selectedChoiceIdx}
              onSelect={setSelectedChoiceIdx}
            />
          )}

          <RewardList rewards={rewards} />
        </div>

        <Divider />

        {/* Footer */}
        <div className="px-8 py-4 flex flex-col items-center gap-2">
          {(rewards?.item_choice?.length ?? 0) > 1 && selectedChoiceIdx === null && (
            <div
              className="text-[12px] italic"
              style={{ fontFamily: 'EB Garamond, serif', color: '#8A6B2E' }}
            >
              Выберите одну награду
            </div>
          )}
          <button
            type="button"
            onClick={claim}
            disabled={sparking || (
              (rewards?.item_choice?.length ?? 0) > 1 && selectedChoiceIdx === null
            )}
            className="px-8 py-2.5 text-[13px] uppercase tracking-[0.2em] hover:scale-[1.04] transition"
            style={{
              background: 'linear-gradient(180deg, #F8E6B5 0%, #E8C77B 35%, #C9A96A 65%, #8A6B2E 100%)',
              color: '#0B0907',
              border: '2px solid #3A2818',
              fontFamily: 'Cinzel, serif',
              fontWeight: 700,
              cursor: sparking ? 'wait' : 'pointer',
              boxShadow: '0 5px 14px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.4)',
              opacity: (sparking || (
                (rewards?.item_choice?.length ?? 0) > 1 && selectedChoiceIdx === null
              )) ? 0.55 : 1,
            }}
          >
            Получить награду <span style={{ opacity: 0.7, marginLeft: 6 }}>[Z]</span>
          </button>
        </div>
      </div>
    </div>
  );
}

function RewardList({ rewards }: { rewards?: QuestRewards }) {
  if (!rewards) {
    return (
      <div className="text-center text-[14px] italic" style={{ color: '#8A6B2E' }}>
        Тёплое слово и крепкое рукопожатие.
      </div>
    );
  }
  const items: Array<{ icon: string; label: string; color: string }> = [];
  if (rewards.xp > 0)                                          items.push({ icon: '✧', label: `${rewards.xp} опыта`,         color: '#8A6B2E' });
  if (rewards.gold_aurei && rewards.gold_aurei > 0)            items.push({ icon: '⚜', label: `${rewards.gold_aurei} ауреев`, color: '#C9A96A' });
  if (rewards.crystals && rewards.crystals > 0)                items.push({ icon: '◆', label: `${rewards.crystals} кристаллов`, color: '#3E6FA5' });
  if (rewards.faction_rep && rewards.faction_rep > 0)          items.push({ icon: '✚', label: `+${rewards.faction_rep} славы`,  color: '#5BAE57' });
  // R232q5aK: when multiple choices exist, the picker grid handles them;
  // only inline the single-choice fallback here so it still renders.
  const choices = rewards.item_choice ?? [];
  if (choices.length === 1) {
    const it = choices[0]!;
    items.push({ icon: '◇', label: it.name ?? it.item_id, color: '#8E6FB0' });
  }
  if (items.length === 0) {
    return (
      <div className="text-center text-[14px] italic" style={{ color: '#8A6B2E' }}>
        Тёплое слово и крепкое рукопожатие.
      </div>
    );
  }
  return (
    <ul className="space-y-2">
      {items.map((it, i) => (
        <li
          key={i}
          className="flex items-center gap-3 px-4 py-2"
          style={{
            background: 'rgba(232,220,192,0.55)',
            border: `1px solid ${it.color}`,
            fontFamily: 'EB Garamond, serif',
          }}
        >
          <span
            className="text-[20px]"
            style={{
              color: it.color,
              textShadow: '0 1px 0 rgba(255,255,255,0.4)',
            }}
            aria-hidden
          >
            {it.icon}
          </span>
          <span className="text-[15px]" style={{ color: '#3A2818' }}>
            {it.label}
          </span>
        </li>
      ))}
    </ul>
  );
}

/**
 * R232q5aK: choice grid for multi-item quest rewards. Renders a parchment-
 * tinted tile per item; the selected tile gets a gold border. Click sets
 * the index back via `onSelect`.
 */
function ChoicePicker(props: {
  choices: Array<{ item_id: string; name?: string }>;
  selectedIdx: number | null;
  onSelect: (idx: number) => void;
}) {
  return (
    <div className="mb-4">
      <div
        className="text-[10px] uppercase tracking-[0.32em] mb-2 text-center"
        style={{ fontFamily: 'Spectral SC, serif', color: '#8A6B2E' }}
      >
        Выбор награды
      </div>
      <div className="grid grid-cols-3 gap-2">
        {props.choices.map((it, i) => {
          const sel = props.selectedIdx === i;
          return (
            <button
              key={`${it.item_id}-${i}`}
              type="button"
              onClick={() => props.onSelect(i)}
              className="px-3 py-3 text-left transition"
              style={{
                background: sel
                  ? 'linear-gradient(180deg, #F8E6B5 0%, #E8C77B 100%)'
                  : 'rgba(232,220,192,0.55)',
                border: sel ? '2px solid #5C4520' : '1px solid #8E6FB0',
                fontFamily: 'EB Garamond, serif',
                color: '#3A2818',
                cursor: 'pointer',
                boxShadow: sel ? '0 0 12px rgba(232,199,123,0.55)' : 'none',
              }}
            >
              <div className="flex items-center gap-2">
                <span
                  className="text-[18px]"
                  style={{ color: '#8E6FB0', textShadow: '0 1px 0 rgba(255,255,255,0.4)' }}
                  aria-hidden
                >
                  ◇
                </span>
                <span className="text-[13px]">{it.name ?? it.item_id}</span>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function Divider() {
  return (
    <div
      style={{
        height: 1,
        background: 'linear-gradient(90deg, transparent, #8A6B2E 25%, #8A6B2E 75%, transparent)',
        opacity: 0.6,
      }}
    />
  );
}

function Corner({ pos }: { pos: 'tl' | 'tr' | 'bl' | 'br' }) {
  const cls: Record<typeof pos, string> = {
    tl: 'top-2 left-2',
    tr: 'top-2 right-2 scale-x-[-1]',
    bl: 'bottom-2 left-2 scale-y-[-1]',
    br: 'bottom-2 right-2 scale-x-[-1] scale-y-[-1]',
  };
  return (
    <svg
      className={`absolute ${cls[pos]} w-7 h-7 pointer-events-none`}
      viewBox="0 0 28 28"
      fill="none"
      stroke="#5C4520"
      strokeWidth="1.5"
    >
      <path d="M2 2 L10 2 M2 2 L2 10" />
      <path d="M3 3 Q9 3 9 9" />
      <circle cx="5" cy="5" r="1" fill="#5C4520" />
    </svg>
  );
}
